import {
  extractSupportProps,
  WithLayoutType,
  WithSupportProps,
} from '@sbercloud/uikit-product-utils';
import { Tabs } from '@snack-uikit/tabs';
import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';

import { ProductCategory } from '../../types';
import {
  SectionCatalogFooterProps,
  SectionCatalogWithSearchFooter,
} from './components';
import {
  SectionCatalogWithSearchSkeleton,
  SectionCatalogWithSearchTabBodyItem,
  SectionTitleCatalogMain,
} from './components';
import styles from './styles.module.scss';

export type SectionCatalogProps = WithSupportProps<
  WithLayoutType<{
    /** id секции */
    id?: string;
    /** CSS-класс */
    className?: string;
    /** Заголовок */
    title?: string;
    /** Список продуктов по категориям */
    categories: ProductCategory[];
    /** Выбранная категория продуктов */
    selectedCategory?: string;
    /** Платформа */
    platformFilter?: string;
    /** Колбек смены выбранной категории продуктов */
    onCategorySelect?(category: string): void;
    /** Колбек смены выбранного типа продуктов */
    onChangeSection?(): void;
    /** Список элементов для футера в каталоге */
    footer?: SectionCatalogFooterProps['items'];
    /** ReactNode, встаривающийся в нижний блок компонента */
    bottomContent?: React.ReactNode;
    /** Флаг состояния поиска */
    isSearching?: boolean;
    /** Флаг состояния загрузки */
    isLoading?: boolean;
    // Todo: в будущем убрать
    isOldCard?: boolean;
    onClickForClose?: () => void;
  }>
>;

export function SectionCatalogWithSearch({
  id,
  layoutType,
  className,
  title,
  isOldCard,
  selectedCategory: selectedCategoryProp,
  onCategorySelect,
  categories,
  footer,
  bottomContent,
  isSearching,
  isLoading,
  onChangeSection,
  platformFilter,
  onClickForClose,
  ...rest
}: SectionCatalogProps) {
  const selectedCategory = selectedCategoryProp || (categories[0]?.id ?? '');

  const isDesktop = layoutType === 'desktop';

  const slugCategoryForQuery = categories.find(
    (item) => item.id === selectedCategory,
  )?.labelQuery;

  return (
    <div
      id={id}
      title={title}
      data-layout-type={layoutType}
      className={cn(className, styles.sectionCatalog)}
      {...extractSupportProps(rest)}
    >
      <SectionTitleCatalogMain title={title} layoutType={layoutType} />
      <div
        className={cn(styles.body, {
          [styles.withoutTitle]: !title,
        })}
        data-with-title={!title}
        data-layout-type={layoutType}
      >
        <Tabs
          value={selectedCategory}
          onChange={(id) => {
            onCategorySelect && onCategorySelect(id);
            onChangeSection && onChangeSection();
          }}
        >
          <div
            className={cn(styles.categoryList, {
              [styles.desktopCategoryList]: isDesktop,
            })}
          >
            <Tabs.TabBar
              type="primary"
              orientation={isDesktop ? 'vertical' : 'horizontal'}
              markerPosition={isDesktop ? 'before' : 'after'}
              className={cn({ [styles.desktopTabs]: isDesktop })}
            >
              {categories.map(({ id, label }) => (
                <Tabs.Tab key={id} value={id} label={label} />
              ))}
            </Tabs.TabBar>

            {footer && isDesktop && (
              <SectionCatalogWithSearchFooter
                items={footer}
                layoutType={layoutType}
              />
            )}
          </div>

          {categories.map(({ id, list, label }) => (
            <Tabs.TabContent key={id} value={id} className={styles.tabContent}>
              <Typography family="sans" purpose="headline" size="s">
                {!isSearching && list.length > 0 && label}
                {isSearching && list.length > 0 && 'Результаты поиска'}
                {list.length === 0 && 'Ничего не найдено'}
              </Typography>
              {!isLoading ? (
                list.map((elementList) => (
                  <SectionCatalogWithSearchTabBodyItem
                    key={elementList.id}
                    isOldCard={isOldCard}
                    elementList={elementList}
                    layoutType={layoutType}
                    maxLength={elementList.maxLength}
                    platformFilter={platformFilter}
                    slugCategoryForQuery={slugCategoryForQuery}
                    isLinkWithQuery={elementList.id === 'products'}
                    onClickForClose={onClickForClose}
                  />
                ))
              ) : (
                <SectionCatalogWithSearchSkeleton />
              )}
              {bottomContent}
            </Tabs.TabContent>
          ))}
        </Tabs>

        {footer && !isDesktop && (
          <SectionCatalogWithSearchFooter
            items={footer}
            layoutType={layoutType}
          />
        )}
      </div>
    </div>
  );
}
